import { 
  IonContent, IonHeader, IonPage, IonTitle, 
  IonToolbar, IonButtons, IonList, IonItem,
  IonLabel, IonListHeader, IonToggle, 
  useIonViewWillEnter, IonSelect, IonSelectOption,
  IonIcon
} from '@ionic/react';
import { useEffect, useState } from 'react';
import { sunny, moon } from 'ionicons/icons';
import OnlineIndicator from '../components/OnlineIndicator';
import StorageMonitor from '../components/StorageMonitor';
import { useAppPreferences } from '../hooks/preferencesHooks';
import { useTheme } from '../hooks/themeHooks';
import './styles/Settings.css';

const Settings: React.FC = () => {
  const { preferences, setDefaultDivision, setDefaultSorting, isLoading } = useAppPreferences();
  const { isDarkMode, setTheme } = useTheme();
  const [ refreshTrigger, setRefreshTrigger ] = useState<number>(0);

  // Listen for storage updates
  useEffect(() => {
    const handleStorageUpdate = () => {
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('storage', handleStorageUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, []);

  // Refresh storage info whenever the page becomes active (tab changes or page loads)
  useIonViewWillEnter(() => {
    // Increment refresh trigger to force storage check
    setRefreshTrigger(prev => prev + 1);
  });

  // Toggle between light and dark mode
  const toggleTheme = () => {
    setTheme(isDarkMode ? 'light' : 'dark');
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Settings</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Settings</IonTitle>
            <IonButtons slot="end">
              <OnlineIndicator />
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        {/* Storage Section */}
        <IonList inset={true}>
          <IonListHeader>
            <IonLabel>Storage Usage</IonLabel>
          </IonListHeader>
          <StorageMonitor refreshTrigger={refreshTrigger} />
        </IonList>

        {/* Preferences Section */}
        <IonList inset={true} lines="none">
          <IonListHeader>
            <IonLabel>Preferences</IonLabel>
          </IonListHeader>
          <IonItem className="settings-item">
            <IonLabel slot="start">
              Default Train Division
            </IonLabel>
            <IonSelect
              className="settings-select"
              slot="end"
              disabled={isLoading}
              value={preferences.defaultDivision}
              onIonChange={(e) => setDefaultDivision(e.detail.value)}
              aria-label="default division"
            >
              <IonSelectOption value="A Division">A Division</IonSelectOption>
              <IonSelectOption value="B Division">B Division</IonSelectOption>
            </IonSelect>
          </IonItem>
          <IonItem className="settings-item">
            <IonLabel slot="start">
              Sort Calculations By
            </IonLabel>
            <IonSelect
              className="settings-select"
              slot="end"
              disabled={isLoading}
              value={preferences.defaultSorting}
              onIonChange={(e) => setDefaultSorting(e.detail.value)}
              aria-label="default sorting"
            >
              <IonSelectOption value="Newest First">Newest First</IonSelectOption>
              <IonSelectOption value="Oldest First">Oldest First</IonSelectOption>
              <IonSelectOption value="Alphabetical">Alphabetical</IonSelectOption>
            </IonSelect>
          </IonItem>
        </IonList>

        {/* Theme Section */}
        <IonList inset={true} lines="none">
          <IonListHeader>
            <IonLabel>Appearance</IonLabel>
          </IonListHeader>
          <IonItem>
            <IonIcon icon={isDarkMode ? sunny : moon} slot="start" />
            <IonLabel>{isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}</IonLabel>
            <IonToggle 
              checked={isDarkMode}
              onIonChange={toggleTheme}
              disabled={isLoading}
              slot="end"
              aria-label="Dark mode toggle"
            />
          </IonItem>
        </IonList>

      </IonContent>
    </IonPage>
  );
};

export default Settings;